<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import UIKit from '@pocketprep/ui-kit'
import Banner from '@/components/ComplexBanner.vue'
import { integrationsModule } from '@/store/integrations/module'
import { toastModule } from '@/store/toast/module'
import { analyticsModule } from '@/store/analytics/module'
import { organizationModule } from '@/store/organization/module'

const { SidePanel, Button: PocketButton, Input: PocketInput, Errors } = UIKit

const emit = defineEmits<{
    close: [value?: boolean]
}>()

const clientId = ref('')
const clientIdRegex = /^[0-9]+$/
const isLoading = ref(false)
const isClientIdValidated = ref(false)
const errors = ref<string[]>([])

// eslint-disable-next-line max-len
const viewInstructionsLink = 'https://help.educators.pocketprep.com/en/articles/9953683-integrating-a-new-canvas-instance-with-pocket-prep'

const clearClientIdValidated = () => {
    isClientIdValidated.value = false
}

const currentIntegrations = computed(() => {
    return integrationsModule.getters.getCurrentIntegrations()
})

const currentOrganization = computed(() => {
    return organizationModule.getters.getCurrentOrganization()
})

const isValidClientId = computed(() => {
    return clientIdRegex.test(clientId.value)
        && !currentIntegrations.value.some(ci => ci.clientId === clientId.value)
})

const emitClose = () => {
    emit('close', true)
}

const connectIntegration = async () => {
    isLoading.value = true

    await integrationsModule.actions.createLMSIntegration({
        clientId: clientId.value,
    })
    toastModule.actions.displayToast({
        title: 'Canvas LMS connected.',
    })

    if (currentOrganization.value) {
        analyticsModule.actions.amplitudeTrack('LMS Integration Created', {
            organizationId: currentOrganization.value?.objectId,
            organizationName: currentOrganization.value?.name,
        })
    }

    await integrationsModule.actions.fetchLMSIntegrations(true)
    emitClose()
    isLoading.value = false
}

const validateClientId = () => {
    if (clientId.value && !isValidClientId.value) {
        errors.value.push('The Client ID you entered is not valid.')
    } else {
        errors.value = []
    }
    isClientIdValidated.value = true
}

const handlePastedClientId = async (event: ClipboardEvent) => {
    await new Promise(res => setTimeout(res, 1))
    const pastedClientId = event.clipboardData?.getData('text')
    if (pastedClientId) {
        clientId.value = pastedClientId
        validateClientId()
    }
}

onMounted(async () => {
    await Promise.all([
        organizationModule.actions.fetchCurrentOrganization(),
    ])
})
</script>

<template>
    <Portal to="sidepanel">
        <SidePanel
            class="create-integration"
            name="Connect Canvas LMS"
            :isTeachSidePanel="true"
            @close="emitClose"
        >
            <Banner
                class="create-integration__banner"
                title="A Canvas admin will be needed for this integration."
                :link-url="viewInstructionsLink"
            >
                We have a guide to walk you through configuring your Canvas integration with Pocket Prep.
                Once this is done, you will be able to add a Pocket Prep SSO link to Canvas courses.
            </Banner>
            <PocketInput
                v-model="clientId"
                class="create-integration__client-id"
                field-type="text"
                label="Client ID"
                :error="!!errors.length"
                @paste="handlePastedClientId"
                @focusout="validateClientId"
                @keydown="clearClientIdValidated"
            />
            <Errors
                v-if="errors.length"
                class="create-integration__error-banner"
                :errors="errors"
            />
            <template #action>
                <PocketButton
                    class="create-integration__connect-btn"
                    :disabled="isLoading || !clientId || (!!errors.length && !!clientId) || !isClientIdValidated"
                    @click="connectIntegration"
                >
                    Connect Integration
                </PocketButton>
            </template>
        </SidePanel>
    </Portal>
</template>

<style lang="scss" scoped>
.create-integration {

    &__client-id {
        margin-top: 24px;
    }

    &__error-banner {
        margin-top: 12px;
    }

    &__label {
        font-size: 13px;
        font-weight: 600;
        line-height: 18px;
        margin: 0 0 4px 12px;
        color: $pickled-bluewood;
        display: block;
    }

    &__input {
        appearance: none;
        background-color: $white;
        border: 1px solid rgba($pewter, 0.85);
        color: $brand-black;
        border-radius: 3px;
        caret-color: $brand-blue;
        padding: 9px 11px;
        font-size: 15px;
        line-height: 20px;
        height: 36px;
        width: 100%;
        box-sizing: border-box;
        outline: none;
        z-index: 2;
    }
}
</style>
